import React, { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import ActivityHeader from "./ActivityHeader";
import GroupList from "./GroupList";
import { HEADER_HEIGHT } from "../Header";
import { getAllUsers } from "api/users";
import { getActiveConferences, getUnansweredConferences } from "api/conferences";
import { useDispatch } from "react-redux";
import { setOtherUserCalls } from "reducers/slices/callSlotReducer";
import useCallFunctions from "hooks/useCallFunctions";
import { addCalls } from "reducers/slices/callReducer";
import { prepareFetchedCalls } from "utils/calls";
import { setAndGroupUsers, setUserOffline, setUserOnline } from "reducers/slices/activityViewReducer";
import { getCallStats } from "api/callrecords";
import { useAppSelector } from "reducers/hooks";
import useSocket from "../../../hooks/useSocket";
import {logger} from "utils/logger";

interface TeamCallStats {
    teamId: string,
    callsPerDay: number,
}

export interface CallStats {
    totalAnsweredCallsPerDay: number,
    totalUnansweredCallsPerDay: number,
    teams: TeamCallStats[],
}

const ActivityView = () => {
    const socket = useSocket();
    const loggedInUser = useAppSelector(state => state.user.user);
    const userId = loggedInUser?.userId;
    const dispatch = useDispatch()
    const { addUnansweredCall } = useCallFunctions()
    const [callStats, setCallStats] = useState<CallStats>({ totalAnsweredCallsPerDay: 0, totalUnansweredCallsPerDay: 0, teams: [] })
    const [showInactiveUsers, setShowInactiveUsers] = useState<boolean>(false);

    useEffect(() => {
        if (userId) {
            const storedValue = localStorage.getItem(`showInactiveUsers_${userId}`);
            if (storedValue !== null) {
                setShowInactiveUsers(JSON.parse(storedValue));
            }
        }
    }, [userId]);

    useEffect(() => {
        if (userId) {
            localStorage.setItem(`showInactiveUsers_${userId}`, JSON.stringify(showInactiveUsers));
        }
    }, [showInactiveUsers, userId]);

    useEffect(() => {
      getCallStats().then(res => {
        setCallStats(res.data)
      })

      getAllUsers().then((res: any) => {
        dispatch(setAndGroupUsers(res.data))
      })

      getActiveConferences().then((res: any) => {
        const _calls = prepareFetchedCalls(res.data)
        const calls = _calls.filter(e => e.conferenceJoinedBy !== loggedInUser?.userId)
        dispatch(addCalls({ calls }))
        dispatch(setOtherUserCalls(res.data))
      })

      getUnansweredConferences().then(res => {
        res.data.forEach((conference: any) => {
          addUnansweredCall(conference.conferenceName, conference)
        })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedInUser?.details?.teamBlock, loggedInUser?.details?.userBlock])

    useEffect(() => {
      socket?.emit('ACTIVITY_VIEW_SUB', {}, function () {
        logger('ACTIVITY_VIEW_SUB')
      });

      return () => {
        socket?.emit('ACTIVITY_VIEW_UNSUB', {}, function () {
          logger('ACTIVITY_VIEW_UNSUB')
        });
      }
    }, [socket])

    useEffect(() => {
        if (!socket) {
            return;
        }

        const handleUserOnline = ({ userId }: { userId: string }) => {
            logger(`Socket event: USER_ONLINE for userId: ${userId}`);
            dispatch(setUserOnline(userId));
        };

        const handleUserOffline = ({ userId }: { userId:string }) => {
            logger(`Socket event: USER_OFFLINE for userId: ${userId}`);
            dispatch(setUserOffline(userId));
        };

        socket.on('USER_ONLINE', handleUserOnline);
        socket.on('USER_OFFLINE', handleUserOffline);

        return () => {
            socket.off('USER_ONLINE', handleUserOnline);
            socket.off('USER_OFFLINE', handleUserOffline);
        };
    }, [socket, dispatch]);


    return (
      <Box width="100%" height={`calc(100% - ${HEADER_HEIGHT}px)`}>
        <ActivityHeader callStats={callStats} showInactiveUsers={showInactiveUsers} setShowInactiveUsers={setShowInactiveUsers} />
        <GroupList callStats={callStats} showInactiveUsers={showInactiveUsers} />
      </Box>
    );
};

export default ActivityView;